import { defineMessages } from "react-intl";

import type { FsvSaveErrorRule } from "./ListingFsvForm.types";

/** spaces-v2 `full_service.room_type` values. */
const ROOM_TYPES = [
  "full_space_buyout",
  "private_room",
  "semi_private_area",
  "reserved_area",
] as const;

type RoomType = (typeof ROOM_TYPES)[number];

/** The lowest space rental a host can charge, in whole units. */
const MIN_SPACE_RENTAL = 1;

/** The gratuity rate range a host can charge, in whole percent. */
const MIN_GRATUITY_RATE = 1;
const MAX_GRATUITY_RATE = 100;

/** Amounts rounded up to whole numbers, as custom offer does. */
const ROUNDED_UP_FIELDS = [
  "foodAndDrinkMinimum",
  "spaceRental",
  "gratuityRate",
] as const;

// defineMessages so `extract-main` picks these up; they're only ever
// formatted through a variable
const ROUNDED_UP_MESSAGES = defineMessages({
  amount: {
    id: "hosting.listing_fsv.rounded_up_amount_toast",
    defaultMessage: "Rounded up to {amount}.",
  },
  percent: {
    id: "hosting.listing_fsv.rounded_up_percent_toast",
    defaultMessage: "Rounded up to {amount}%.",
  },
});

// defineMessages so `extract-main` picks these up; they're only ever
// formatted through a variable
const SAVE_ERROR_MESSAGES = defineMessages({
  belowMinimumSpendCombined: {
    id: "hosting.listing_fsv.error_below_minimum_spend_combined",
    defaultMessage:
      "The combined food and drink minimum and space rental amount must be at least {amount}.",
  },
  belowMinimumSpendFoodAndDrink: {
    id: "hosting.listing_fsv.error_below_minimum_spend_food_and_drink",
    defaultMessage: "Minimum spend must be at least {amount}.",
  },
  belowMinimumSpend: {
    id: "hosting.listing_fsv.error_below_minimum_spend",
    defaultMessage:
      "Your food and drink minimum and space rental together are below the minimum spend. Increase one of them.",
  },
  noFlatRateTier: {
    id: "hosting.listing_fsv.error_no_flat_rate_tier",
    defaultMessage:
      "We can't set a space rental for this food and drink minimum. Adjust the minimum or turn off the space rental.",
  },
  flatRateBelowFloor: {
    id: "hosting.listing_fsv.error_flat_rate_below_floor",
    defaultMessage:
      "Your space rental is below the minimum for this food and drink minimum.",
  },
  pricingUnavailable: {
    id: "hosting.listing_fsv.error_pricing_unavailable",
    defaultMessage:
      "We couldn't check your pricing right now. Please try again.",
  },
  submitError: {
    id: "hosting.listing_fsv.submit_error",
    defaultMessage: "Something went wrong. Please try again.",
  },
});

/**
 * How each spaces-v2 pricing `error_code` (from `validate-pricing` and the
 * FSV update) shows on the form. Any other code shows the generic error.
 */
const FSV_SAVE_ERRORS: Record<string, FsvSaveErrorRule> = {
  // F&B minimum alone (no space rental) is under the deposit-model minimum
  invalid_f_and_b_min: {
    field: "foodAndDrinkMinimum",
    message: SAVE_ERROR_MESSAGES.belowMinimumSpendFoodAndDrink,
    amountKey: "f_and_b_amount_min",
  },
  // Space rental + F&B minimum is under the deposit-model minimum; it lands
  // on the F&B minimum
  invalid_combined_minimum: {
    field: "foodAndDrinkMinimum",
    message: SAVE_ERROR_MESSAGES.belowMinimumSpendCombined,
    amountKey: "combined_amount_min",
  },
  // No flat-rate tier covers this F&B minimum
  no_flat_rate_tier: {
    field: "foodAndDrinkMinimum",
    message: SAVE_ERROR_MESSAGES.noFlatRateTier,
  },
  // Space rental is under its F&B tier's floor
  flat_rate_below_floor: {
    field: "spaceRental",
    message: SAVE_ERROR_MESSAGES.flatRateBelowFloor,
  },
  // The fees service couldn't return the tiers to validate against
  fees_error: {
    message: SAVE_ERROR_MESSAGES.pricingUnavailable,
  },
};

export {
  FSV_SAVE_ERRORS,
  MAX_GRATUITY_RATE,
  MIN_GRATUITY_RATE,
  MIN_SPACE_RENTAL,
  ROOM_TYPES,
  ROUNDED_UP_FIELDS,
  ROUNDED_UP_MESSAGES,
  SAVE_ERROR_MESSAGES,
};
export type { RoomType };
